import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  HStack,
  Stack,
  VStack,
  Text,
  Center,
  ScrollView,
  Button,
  useToast,
  Spinner,
  Pressable,
  Icon,
  Input,
  Image,
  TextArea,
  Modal,
} from "native-base";
import { Dimensions, Platform } from "react-native";
import { GlobalStyles } from "../../constants/styles";
import { useSelector } from "react-redux";
import axios from "axios";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Svg, {
  Path,
  G,
  Defs,
  LinearGradient as SvgLinearGradient,
  Stop,
  Text as SvgText,
  Line,
  Circle,
} from "react-native-svg";
import Toast from "../../components/Toast";
import DatePickerSingleInput from "../../components/DatePickerSingleInput";
import { GlobalConstants } from "../../constants/constants";

const API_BASE_URL = `${GlobalConstants.ReservationBaseUrl}`.replace(/\/$/, "");

const topArrowIcon = require("../../../assets/topArrow.png");
const bottomArrowIcon = require("../../../assets/bottomArrow.png");
const categoryNameIcon = require("../../../assets/categoryName.png");
const otherCategoryIcon = require("../../../assets/otherCategory.png");
const addObjectIcon = require("../../../assets/addObject.png");

const HomeButton = ({ onPress }) => {
  return (
    <Pressable onPress={onPress}>
      <HStack
        alignItems="center"
        space={2}
        bg="rgba(3, 0, 87, 0.1)"
        px={4}
        py={2}
        borderRadius={8}
      >
        <Icon
          as={Ionicons}
          name="home"
          size="md"
          color={GlobalStyles.colors.blue}
        />
        <Text
          fontSize="14px"
          fontWeight="500"
          color={GlobalStyles.colors.blue}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          Home
        </Text>
      </HStack>
    </Pressable>
  );
};

const SearchBar = ({ value, onChange, onClear, onSubmit }) => {
  return (
    <HStack
      w={["90%", "80%", "500px"]}
      borderRadius={10}
      borderWidth={1}
      borderColor="#00000014"
      px={3}
      py={2}
      alignItems="center"
      space={2}
    >
      <Icon as={Ionicons} name="search" color="#00000099" size="md" />
      <Input
        flex={1}
        variant="unstyled"
        value={value}
        onChangeText={onChange}
        placeholder="Search by name or reservation ID"
        placeholderTextColor="#000000A3"
        fontSize="14px"
        onSubmitEditing={onSubmit}
        returnKeyType="search"
        fontFamily={GlobalStyles.text.fontFamily}
      />
      {value?.length > 0 && (
        <Pressable onPress={onClear}>
          <Icon as={Ionicons} name="close-circle" color="#00000099" size="md" />
        </Pressable>
      )}
    </HStack>
  );
};

const MiniTrendChart = ({ data }) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <Box w="95px" h="55px" justifyContent="center" alignItems="center">
        <Text fontSize="8px" color="rgba(255,255,255,0.5)">
          No data
        </Text>
      </Box>
    );
  }

  const values = data.map((item) => item.value || 0);

  if (values.every((v) => v === 0)) {
    return (
      <Box w="95px" h="55px" justifyContent="center" alignItems="center">
        <Text fontSize="8px" color="rgba(255,255,255,0.5)">
          No data
        </Text>
      </Box>
    );
  }
  const width = 95;
  const height = 55;
  const padding = 2;
  const graphWidth = width - padding * 2;
  const graphHeight = height - padding * 2;
  const maxValue = Math.max(...values, 1);
  const minValue = Math.min(...values, 0);
  const range = maxValue - minValue || 1;
  const points = values.map((value, index) => {
    const x = padding + (index / Math.max(values.length - 1, 1)) * graphWidth;
    const y =
      padding + graphHeight - ((value - minValue) / range) * graphHeight;
    return { x, y };
  });
  const createPath = () => {
    if (points.length === 0) return "";
    let path = `M ${points[0].x} ${height}`;
    path += ` L ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;
      path += ` Q ${midX} ${current.y}, ${midX} ${(current.y + next.y) / 2}`;
      path += ` Q ${midX} ${next.y}, ${next.x} ${next.y}`;
    }
    path += ` L ${points[points.length - 1].x} ${height}`;
    path += ` Z`;
    return path;
  };

  const createLine = () => {
    if (points.length === 0) return "";
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;
      path += ` Q ${midX} ${current.y}, ${midX} ${(current.y + next.y) / 2}`;
      path += ` Q ${midX} ${next.y}, ${next.x} ${next.y}`;
    }
    return path;
  };

  const gradientId = `miniGradient-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <Box w={width} h={height}>
      <Svg width={width} height={height}>
        <Defs>
          <SvgLinearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <Stop offset="0" stopColor="#030057" stopOpacity="1" />
            <Stop offset="0.23" stopColor="#030057" stopOpacity="1" />
            <Stop offset="1" stopColor="#0095FF" stopOpacity="0.7" />
          </SvgLinearGradient>
        </Defs>
        <G>
          <Path d={createPath()} fill={`url(#${gradientId})`} />
          <Path
            d={createLine()}
            stroke="#030057"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </G>
      </Svg>
    </Box>
  );
};
const ReservationChart = ({
  selectedMetric,
  selectedPeriod,
  onMetricChange,
  onPeriodChange,
  openChartDropdown,
  setOpenChartDropdown,
  chartData,
}) => {
  const [dimensions, setDimensions] = useState(Dimensions.get("window"));

  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  const screenWidth = dimensions.width;

  const getChartDimensions = () => {
    if (screenWidth < 450) {
      return { width: screenWidth - 32, height: 250 };
    } else if (screenWidth < 768) {
      return { width: Math.min(screenWidth - 60, 450), height: 300 };
    } else {
      return { width: Math.min(screenWidth - 100, 550), height: 330 };
    }
  };
const { width: chartWidth, height: chartHeight } = getChartDimensions();
const data = chartData || { labels: [], values: [] };
if (
    !data.labels ||
    data.labels.length === 0 ||
    !data.values ||
    data.values.length === 0
  ) {
    return (
      <Box
        w="100%"
        maxW="600px"
        bg={GlobalStyles.colors.white}
        borderRadius={12}
        p={4}
      >
        <VStack space={3}>
          <HStack
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <Text fontSize="14px" fontWeight="500" color="#1a1a1a">
              {selectedMetric}
            </Text>
          </HStack>
          <Center h={chartHeight}>
            <Text color="#9CA3AF">No data available</Text>
          </Center>
        </VStack>
      </Box>
    );
  }
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 20;
  const paddingBottom = 45;
  const graphWidth = chartWidth - paddingLeft - paddingRight;
  const graphHeight = chartHeight - paddingTop - paddingBottom;
  const maxValue = Math.max(...data.values, 10);
  const yTicks = 5;
  const yStep = maxValue / yTicks;
  const points = data.values.map((value, index) => {
    const x =
      paddingLeft + (index / Math.max(data.labels.length - 1, 1)) * graphWidth;
    const y = paddingTop + graphHeight - (value / maxValue) * graphHeight;
    return { x, y, value };
  });

  const createPath = () => {
    if (points.length === 0) return "";
    const baselineY = paddingTop + graphHeight;
    let path = `M ${points[0].x} ${baselineY} `;
    path += `L ${points[0].x} ${points[0].y} `;
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;
      path += `Q ${midX} ${current.y}, ${midX} ${(current.y + next.y) / 2} `;
      path += `Q ${midX} ${next.y}, ${next.x} ${next.y} `;
    }
    path += `L ${points[points.length - 1].x} ${baselineY} `;
    path += `Z`;
    return path;
  };

  const createSmoothPath = () => {
    if (points.length === 0) return "";
    let path = `M ${points[0].x} ${points[0].y} `;
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const midX = (current.x + next.x) / 2;
      path += `Q ${midX} ${current.y}, ${midX} ${(current.y + next.y) / 2} `;
      path += `Q ${midX} ${next.y}, ${next.x} ${next.y} `;
    }
    return path;
  };

  const getLabelsToDisplay = () => {
    const totalLabels = data.labels.length;
    const maxLabelsToShow = 7;

    if (totalLabels <= maxLabelsToShow) {
      return data.labels.map((label, index) => ({ label, index }));
    }

    const labelsToShow = [];
    const step = Math.floor(totalLabels / (maxLabelsToShow - 1));

    for (let i = 0; i < maxLabelsToShow - 1; i++) {
      const index = i * step;
      labelsToShow.push({ label: data.labels[index], index });
    }

    labelsToShow.push({
      label: data.labels[totalLabels - 1],
      index: totalLabels - 1,
    });

    return labelsToShow;
  };

  const labelsToDisplay = getLabelsToDisplay();

  return (
    <Box
      w="100%"
      maxW="600px"
      bg={GlobalStyles.colors.white}
      borderRadius={12}
      p={4}
    >
      <VStack space={3}>
        <HStack
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          zIndex={0}
        >
          <Text
            fontSize="14px"
            fontWeight="500"
            color="#1a1a1a"
            mb={screenWidth < 450 ? 2 : 0}
          >
            Total Reservations
          </Text>
        </HStack>

        <Box w="100%" zIndex={0}>
          <Svg width={chartWidth} height={chartHeight}>
            {[...Array(yTicks + 1)].map((_, i) => {
              const value = Math.round((yTicks - i) * yStep);
              const y = paddingTop + (i / yTicks) * graphHeight;
              return (
                <G key={i}>
                  <SvgText
                    x={paddingLeft - 10}
                    y={y + 5}
                    textAnchor="end"
                    fontSize="12"
                    fill="#6B7280"
                  >
                    {value}
                  </SvgText>
                  <Line
                    x1={paddingLeft}
                    y1={y}
                    x2={chartWidth - paddingRight}
                    y2={y}
                    stroke="#E5E7EB"
                    strokeWidth="1"
                    strokeDasharray="4,4"
                  />
                </G>
              );
            })}

            <Defs>
              <SvgLinearGradient
                id="areaGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <Stop offset="0%" stopColor="#030057" stopOpacity="1" />
                <Stop offset="30%" stopColor="#030057" stopOpacity="0.9" />
                <Stop offset="70%" stopColor="#0095FF" stopOpacity="0.3" />
                <Stop offset="100%" stopColor="#0095FF" stopOpacity="0.05" />
              </SvgLinearGradient>
            </Defs>

            <Path d={createPath()} fill="url(#areaGradient)" />
            <Path
              d={createSmoothPath()}
              fill="none"
              stroke="#030057"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {points.map((point, i) => (
              <G key={i}>
                <Circle cx={point.x} cy={point.y} r="5" fill="#030057" />
                <Circle cx={point.x} cy={point.y} r="3" fill="#FFFFFF" />
              </G>
            ))}

            {labelsToDisplay.map(({ label, index }) => {
              const x =
                paddingLeft +
                (index / Math.max(data.labels.length - 1, 1)) * graphWidth;
              return (
                <SvgText
                  key={index}
                  x={x}
                  y={chartHeight - 15}
                  textAnchor="middle"
                  fontSize="12"
                  fill="#6B7280"
                >
                  {label}
                </SvgText>
              );
            })}
          </Svg>
        </Box>
      </VStack>
    </Box>
  );
};

const CheckReservationView = ({ onHome }) => {
  const [query, setQuery] = useState("");
  const [checkedInState, setCheckedInState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reservationData, setReservationData] = useState(null);
  const toast = useToast();
  let user = useSelector((state) => state.user.user);

  const searchReservation = async (searchQuery) => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/reservations`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (response.data && Array.isArray(response.data)) {
        const searchLower = searchQuery.toLowerCase().trim();
        const foundReservation = response.data.find(
          (reservation) =>
            reservation.id.toLowerCase() === searchLower ||
            reservation.customer_name.toLowerCase().includes(searchLower),
        );

        if (foundReservation) {
          setReservationData(foundReservation);
          setCheckedInState(true);
          toast.show({
            render: () => (
              <Toast
                color={GlobalStyles.colors.green}
                message={"Reservation found successfully!"}
              />
            ),
            placement: "top",
          });
        } else {
          throw new Error("Reservation not found");
        }
      }
    } catch (error) {
      console.log("Error fetching reservation:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Reservation not found!"}
          />
        ),
        placement: "top",
      });
      setCheckedInState(false);
      setReservationData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkCheckedIn = () => {
    toast.show({
      render: () => (
        <Toast
          color={GlobalStyles.colors.green}
          message={"User marked as checked in!"}
        />
      ),
      placement: "top",
    });
  };

  const onSubmit = useCallback(async () => {
    if (query && query.trim().length > 0) {
      await searchReservation(query);
    }
  }, [query, user?.token]);

  const onClear = useCallback(() => {
    setQuery("");
    setCheckedInState(false);
    setReservationData(null);
  }, []);

  const onScanQR = useCallback(() => {
    toast.show({
      render: () => (
        <Toast
          color={GlobalStyles.colors.blue}
          message={
            "QR scanner not available. Enter reservation ID or name."
          }
        />
      ),
      placement: "top",
    });
  }, []);

  return (
    <VStack w="100%" space={4}>
      <HStack w="100%" justifyContent="flex-end" px={4} mt={2}>
        <HomeButton onPress={onHome} />
      </HStack>

      <VStack w="100%" alignItems="center" space={6} mt={1} minH="500px">
        <VStack w="90%" maxW="500px" space={2} px={4}>
        </VStack>

        <SearchBar
          value={query}
          onChange={setQuery}
          onClear={onClear}
          onSubmit={onSubmit}
        />

        <Box w="100%" alignItems="center" minH="400px">
          {isLoading && <Spinner color={GlobalStyles.colors.blue} size="lg" />}

          {!checkedInState && !isLoading && (
            <>
              <Text
                color="#00000099"
                fontSize="13px"
                fontWeight="300"
                fontFamily={GlobalStyles.text.fontFamily}
              >
                Or Scan QR
              </Text>

              <Pressable onPress={onScanQR}>
                <Box
                  w="180px"
                  h="180px"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Icon
                    as={Ionicons}
                    name="qr-code-outline"
                    size="150px"
                    color={GlobalStyles.colors.blue}
                  />
                </Box>
              </Pressable>
            </>
          )}

          {checkedInState && reservationData && (
            <VStack space={6} alignItems="center" mt={8} w="100%" maxW="500px" px={4}>
              <VStack space={3} alignItems="center">
                <Text
                  fontSize="18px"
                  fontWeight="600"
                  color={GlobalStyles.colors.blue}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  {reservationData.customer_name}
                </Text>
                <Text
                  fontSize="14px"
                  color="#666"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Reservation ID: {reservationData.id}
                </Text>
                <Text
                  fontSize="14px"
                  color="#666"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Seat: {reservationData.seat_no}
                </Text>
                <Text
                  fontSize="14px"
                  color="#666"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Date: {reservationData.date} | Time: {reservationData.time}
                </Text>
                <Text
                  fontSize="14px"
                  color="#666"
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Payment: {reservationData.payment_type}
                </Text>
              </VStack>

              <Pressable onPress={handleMarkCheckedIn}>
                <Box
                  borderRadius={12}
                  overflow="hidden"
                  borderWidth={1}
                  borderColor={GlobalStyles.colors.blue}
                >
                  <LinearGradient
                    colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                    start={{ x: 0.5, y: 0 }}
                    end={{ x: 0.5, y: 1 }}
                    style={{
                      paddingVertical: 12,
                      paddingHorizontal: 32,
                      alignItems: "center",
                    }}
                  >
                    <HStack space={2} alignItems="center">
                      <Icon
                        as={Ionicons}
                        name="checkmark-circle"
                        size="md"
                        color={GlobalStyles.colors.mustard}
                      />
                      <Text
                        fontSize="14px"
                        fontWeight="500"
                        color={GlobalStyles.colors.mustard}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Mark User Checked In
                      </Text>
                    </HStack>
                  </LinearGradient>
                </Box>
              </Pressable>

              {reservationData.qr_code && (
                <VStack space={2} alignItems="center" mt={4}>
                  <Text
                    fontSize="14px"
                    fontWeight="500"
                    color={GlobalStyles.colors.blue}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    QR Code
                  </Text>
                  <Image
                    source={{ uri: reservationData.qr_code }}
                    alt="QR Code"
                    style={{
                      width: 200,
                      height: 200,
                    }}
                    resizeMode="contain"
                  />
                </VStack>
              )}
            </VStack>
          )}
        </Box>
      </VStack>
    </VStack>
  );
};
const AddSingleSlotModal = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  onSuccess,
  toast,
  user,
}) => {
  const [slotName, setSlotName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [storedCategoryId, setStoredCategoryId] = useState(null);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) =>
      setDimensions(window)
    );
    return () => sub?.remove();
  }, []);
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    if (isOpen && categoryId) {
      console.log("✅ Storing category ID:", categoryId);
      setStoredCategoryId(categoryId);
    }
  }, [isOpen, categoryId]);

  useEffect(() => {
    if (!isOpen) {
      setSlotName("");
    }
  }, [isOpen]);

  const handleAddSlot = async () => {
    if (!slotName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Please enter a slot name!"
          />
        ),
        placement: "top",
      });
      return;
    }
    const finalCategoryId = categoryId || storedCategoryId;

    if (!finalCategoryId) {
      console.error("❌ Category ID is missing!");
      console.error("categoryId prop:", categoryId);
      console.error("storedCategoryId:", storedCategoryId);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Category ID is missing! try again."
          />
        ),
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      const requestData = {
        category_id: finalCategoryId,
        name: slotName.trim(),
      };
      const response = await axios.post(
        `${API_BASE_URL}/api/objects`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        }
      );
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Slot added successfully!"
          />
        ),
        placement: "top",
      });

      setSlotName("");
      setStoredCategoryId(null);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("❌ Error adding slot:");
      console.error("Error response:", err.response?.data);
      console.error("Error message:", err.message);
      
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={err.response?.data?.message || "Failed to add slot!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      useRNModal={false}   
    >
      <Modal.Content maxWidth={isMobile ? "90%" : "400px"}>
        <Modal.CloseButton />
        <Modal.Header>
          <Text fontSize="16px" fontWeight="600" color={GlobalStyles.colors.blue}>
            Add Slot to {categoryName}
          </Text>
        </Modal.Header>

        <Modal.Body>
          <VStack space={4} pb={4}>
            <HStack
              alignItems="center"
              space={3}
              borderWidth={1}
              borderColor="#030061"
              borderRadius={30}
              px={4}
              py={2}
            >
              <Image
                source={addObjectIcon}
                alt="slot"
                style={{ width: 28, height: 28 }}
              />
              <Input
                flex={1}
                variant="unstyled"
                placeholder="Slot Name (e.g. Seat A1)"
                value={slotName}
                onChangeText={setSlotName}
                fontSize="13px"
                color={GlobalStyles.colors.black}
              />
            </HStack>

            <Pressable onPress={handleAddSlot} disabled={isLoading}>
              <Box
                borderRadius={8}
                borderWidth={1}
                borderColor={GlobalStyles.colors.blue}
                opacity={isLoading ? 0.6 : 1}
              >
                <LinearGradient
                  colors={[
                    GlobalStyles.colors.gradientBlueStart,
                    "#007AFF",
                  ]}
                  style={{ paddingVertical: 10, alignItems: "center" }}
                >
                  {isLoading ? (
                    <Spinner
                      size="sm"
                      color={GlobalStyles.colors.mustard}
                    />
                  ) : (
                    <Text color={GlobalStyles.colors.mustard}>
                      Add Slot
                    </Text>
                  )}
                </LinearGradient>
              </Box>
            </Pressable>
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

const EditSlotModal = ({
  isOpen,
  onClose,
  slot,
  onSuccess,
  toast,
  user,
}) => {
  const [slotName, setSlotName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const sub = Dimensions.addEventListener("change", ({ window }) =>
      setDimensions(window)
    );
    return () => sub?.remove();
  }, []);
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    if (slot) {
      setSlotName(slot.name);
    }
  }, [slot]);

  const handleUpdateSlot = async () => {
    if (!slotName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Enter a slot name!"
          />
        ),
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/objects/${slot.id}`,
        {
          name: slotName.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Slot updated successfully!"
          />
        ),
        placement: "top",
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating slot:", err);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={err.response?.data?.message || "Failed to update slot!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" useRNModal={false}>
      <Modal.Content maxWidth={isMobile ? "90%" : "400px"}>
        <Modal.CloseButton />
        <Modal.Header>
          <Text fontSize="16px" fontWeight="600" color={GlobalStyles.colors.blue}>
            Edit Slot
          </Text>
        </Modal.Header>

        <Modal.Body>
          <VStack space={4} pb={4}>
            <HStack
              alignItems="center"
              space={3}
              borderWidth={1}
              borderColor="#030061"
              borderRadius={30}
              px={4}
              py={2}
            >
              <Image
                source={addObjectIcon}
                alt="slot"
                style={{ width: 28, height: 28 }}
              />
              <Input
                flex={1}
                variant="unstyled"
                placeholder="Slot Name"
                value={slotName}
                onChangeText={setSlotName}
                fontSize="13px"
                color={GlobalStyles.colors.black}
              />
            </HStack>

            <Pressable onPress={handleUpdateSlot} disabled={isLoading}>
              <Box
                borderRadius={8}
                borderWidth={1}
                borderColor={GlobalStyles.colors.blue}
                opacity={isLoading ? 0.6 : 1}
              >
                <LinearGradient
                  colors={[
                    GlobalStyles.colors.gradientBlueStart,
                    "#007AFF",
                  ]}
                  style={{ paddingVertical: 10, alignItems: "center" }}
                >
                  {isLoading ? (
                    <Spinner
                      size="sm"
                      color={GlobalStyles.colors.mustard}
                    />
                  ) : (
                    <Text color={GlobalStyles.colors.mustard}>
                      Update Slot
                    </Text>
                  )}
                </LinearGradient>
              </Box>
            </Pressable>
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

const ManageCategorySlotsModal = ({
  isOpen,
  onClose,
  category,
  fetchCategories,
  toast,
  user,
}) => {
  const [showAddSingleSlot, setShowAddSingleSlot] = useState(false);
  const [showEditSlot, setShowEditSlot] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [localSlots, setLocalSlots] = useState([]);
  const [deletingSlotId, setDeletingSlotId] = useState(null);
  const [savedCategoryId, setSavedCategoryId] = useState(null);
  const [savedCategoryName, setSavedCategoryName] = useState(null);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);
  const isMobile = dimensions.width < 768;
  useEffect(() => {
    if (isOpen && category) {
      console.log("ManageCategorySlotsModal opened with category:", {
        id: category.id,
        name: category.name,
        objectsCount: category.objects?.length || 0
      });
      setSavedCategoryId(category.id);
      setSavedCategoryName(category.name);
      setLocalSlots(category.objects || []);
    }
  }, [isOpen, category]);

  useEffect(() => {
    if (category?.objects) {
      setLocalSlots(category.objects);
    }
  }, [category]);

  const handleDeleteSlot = async (slot) => {
    setDeletingSlotId(slot.id);
    try {
      await axios.delete(
        `${API_BASE_URL}/api/objects/${slot.id}`,
        {
          headers: { Authorization: `Bearer ${user?.token}` },
        }
      );

      setLocalSlots((prev) => prev.filter((s) => s.id !== slot.id));
      fetchCategories();

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Slot deleted successfully!"
          />
        ),
        placement: "top",
      });
    } catch (error) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Failed to delete slot!"
          />
        ),
        placement: "top",
      });
    } finally {
      setDeletingSlotId(null);
    }
  };

  const handleSlotAdded = () => {
    fetchCategories();
  };

  const handleEditSlot = (slot) => {
    setSelectedSlot(slot);
    setShowEditSlot(true);
  };

  const handleAddSingleSlot = () => {
    const categoryIdToUse = category?.id || savedCategoryId;
    const categoryNameToUse = category?.name || savedCategoryName;
    if (!categoryIdToUse) {
      console.error("❌ Category ID is missing!");
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Category information is missing. Close and reopen."
          />
        ),
        placement: "top",
      });
      return;
    }
        onClose();
        setTimeout(() => {
      setShowAddSingleSlot(true);
    }, 400);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Content maxWidth={isMobile ? "90%" : "500px"}>
          <Modal.CloseButton />
          <Modal.Header>
            <Text
              fontSize="16px"
              fontWeight="600"
              color={GlobalStyles.colors.blue}
            >
              Manage Slots - {category?.name || savedCategoryName}
            </Text>
          </Modal.Header>

          <Modal.Body>
            <VStack space={4} pb={4}>
              <HStack justifyContent="space-between" alignItems="center">
                <Text fontSize="14px" color="#666">
                  Total Slots: {localSlots.length}
                </Text>
                <Pressable onPress={handleAddSingleSlot}>
                  <HStack alignItems="center" space={1}>
                    <Icon
                      as={Ionicons}
                      name="add-circle"
                      size="sm"
                      color={GlobalStyles.colors.blue}
                    />
                    <Text
                      fontSize="12px"
                      color={GlobalStyles.colors.blue}
                      fontWeight="500"
                    >
                      Add Single
                    </Text>
                  </HStack>
                </Pressable>
              </HStack>

              <ScrollView maxH="400px">
                <VStack space={2}>
                  {localSlots.length > 0 ? (
                    localSlots.map((slot) => (
                      <Box
                        key={slot.id}
                        borderWidth={1}
                        borderColor="#E0E0E0"
                        borderRadius={8}
                        p={3}
                        bg="white"
                      >
                        <HStack justifyContent="space-between" alignItems="center">
                          <Text
                            fontSize="14px"
                            color={GlobalStyles.colors.blue}
                            flex={1}
                          >
                            {slot.name}
                          </Text>

                          <HStack space={3} alignItems="center">
                            <Pressable onPress={() => handleEditSlot(slot)}>
                              <Text
                                fontSize="12px"
                                color={GlobalStyles.colors.blue}
                                fontWeight="500"
                                textDecorationLine="underline"
                              >
                                Edit
                              </Text>
                            </Pressable>
                            
                            <Pressable
                              onPress={() => handleDeleteSlot(slot)}
                              disabled={deletingSlotId === slot.id}
                            >
                              {deletingSlotId === slot.id ? (
                                <Spinner size="sm" />
                              ) : (
                                <Icon
                                  as={Ionicons}
                                  name="trash-outline"
                                  size="sm"
                                  color={GlobalStyles.colors.mustard}
                                />
                              )}
                            </Pressable>
                          </HStack>
                        </HStack>
                      </Box>
                    ))
                  ) : (
                    <Text
                      fontSize="13px"
                      color="#999"
                      textAlign="center"
                      py={8}
                    >
                      No slots in this category
                    </Text>
                  )}
                </VStack>
              </ScrollView>
            </VStack>
          </Modal.Body>
        </Modal.Content>
      </Modal>

      <AddSingleSlotModal
        isOpen={showAddSingleSlot}
        onClose={() => setShowAddSingleSlot(false)}
        categoryId={savedCategoryId}
        categoryName={savedCategoryName}
        onSuccess={handleSlotAdded}
        toast={toast}
        user={user}
      />

      <EditSlotModal
        isOpen={showEditSlot}
        onClose={() => {
          setShowEditSlot(false);
          setSelectedSlot(null);
        }}
        slot={selectedSlot}
        onSuccess={handleSlotAdded}
        toast={toast}
        user={user}
      />
    </>
  );
};
const AddCategoryModal = ({ isOpen, onClose, fetchCategories, toast, user }) => {
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryDescription, setNewCategoryDescription] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);
  const isMobile = dimensions.width < 768;
  
  useEffect(() => {
    if (!isOpen) {
      setNewCategoryName("");
      setNewCategoryDescription("");
      setCategoryImage(null);
    }
  }, [isOpen]);

  const handleImagePick = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            setCategoryImage(event.target.result);
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Enter a category name!"}
          />
        ),
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      const categoryData = {
        name: newCategoryName.trim(),
        description: newCategoryDescription.trim() || undefined,
        image: categoryImage || undefined,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/categories`,
        categoryData,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message={"Category added successfully!"}
            />
          ),
          placement: "top",
        });
        
        setNewCategoryName("");
        setNewCategoryDescription("");
        setCategoryImage(null);
        fetchCategories();
        onClose();
      }
    } catch (error) {
      console.error("Error adding category:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to add category!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Content maxWidth={isMobile ? "90%" : "400px"}>
        <Modal.CloseButton />
        <Modal.Header>
          <Text fontSize="16px" fontWeight="600" color={GlobalStyles.colors.blue}>
            Add Category
          </Text>
        </Modal.Header>
        <Modal.Body>
          <VStack space={4} pb={4}>
            <Text fontSize="11px" color="#666" fontStyle="italic">
              💡 Add category like seats or room classification
            </Text>

            <HStack
              alignItems="center"
              space={3}
              borderWidth={1}
              borderColor="#030061"
              borderRadius={30}
              px={4}
              py={2}
            >
              <Image
                source={categoryNameIcon}
                alt="category"
                style={{ width: 28, height: 28 }}
                resizeMode="contain"
              />
              <Input
                flex={1}
                placeholder="Category Name"
                placeholderTextColor="#666666"
                fontSize="13px"
                fontWeight="400"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                variant="unstyled"
                px={0}
                value={newCategoryName}
                onChangeText={(text) => setNewCategoryName(text)}
                autoFocus={false}
              />
            </HStack>

            <VStack space={2}>
              <Text fontSize="12px" color="#666">
                Description (Optional)
              </Text>
              <TextArea
                placeholder="Add description..."
                placeholderTextColor="#666666"
                fontSize="13px"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                value={newCategoryDescription}
                onChangeText={(text) => setNewCategoryDescription(text)}
                borderColor="#030061"
                borderRadius={12}
                h={20}
                autoCompleteType={undefined}
              />
            </VStack>

            <VStack space={2}>
              <Text fontSize="12px" color="#666">
                Category Image (Optional)
              </Text>
              <Pressable onPress={handleImagePick}>
                <Box
                  borderWidth={2}
                  borderColor={GlobalStyles.colors.blue}
                  borderStyle="dashed"
                  borderRadius={8}
                  p={4}
                  alignItems="center"
                  bg={categoryImage ? "white" : "#F9FAFB"}
                  minH="120px"
                  justifyContent="center"
                >
                  {categoryImage ? (
                    <VStack space={2} alignItems="center">
                      <Image
                        source={{ uri: categoryImage }}
                        alt="Category preview"
                        style={{ width: 100, height: 100, borderRadius: 8 }}
                        resizeMode="cover"
                      />
                      <Text fontSize="11px" color={GlobalStyles.colors.blue} fontWeight="500">
                        Tap to change image
                      </Text>
                    </VStack>
                  ) : (
                    <VStack space={2} alignItems="center">
                      <Icon as={Ionicons} name="cloud-upload-outline" size="2xl" color={GlobalStyles.colors.blue} />
                      <Text fontSize="13px" color={GlobalStyles.colors.blue} fontWeight="500">
                        Tap to Upload Image
                      </Text>
                      <Text fontSize="11px" color="#999">
                        PNG, JPG up to 5MB
                      </Text>
                    </VStack>
                  )}
                </Box>
              </Pressable>
              {categoryImage && (
                <Pressable onPress={() => setCategoryImage(null)}>
                  <HStack space={1} alignItems="center" justifyContent="center">
                    <Icon as={Ionicons} name="trash-outline" size="xs" color={GlobalStyles.colors.red} />
                    <Text fontSize="12px" color={GlobalStyles.colors.red} fontWeight="500">
                      Remove Image
                    </Text>
                  </HStack>
                </Pressable>
              )}
            </VStack>

            <Pressable onPress={handleAddCategory} disabled={isLoading}>
              <Box
                borderRadius={8}
                overflow="hidden"
                borderWidth={1}
                borderColor={GlobalStyles.colors.blue}
                opacity={isLoading ? 0.6 : 1}
              >
                <LinearGradient
                  colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    alignItems: "center",
                  }}
                >
                  {isLoading ? (
                    <Spinner color={GlobalStyles.colors.mustard} size="sm" />
                  ) : (
                    <Text
                      fontSize="13px"
                      fontWeight="500"
                      color={GlobalStyles.colors.mustard}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Add Category
                    </Text>
                  )}
                </LinearGradient>
              </Box>
            </Pressable>
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

const EditCategoryModal = ({
  isOpen,
  onClose,
  category,
  fetchCategories,
  toast,
  user,
}) => {
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [categoryImage, setCategoryImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);
  const isMobile = dimensions.width < 768;

  useEffect(() => {
    if (category) {
      setCategoryName(category.name);
      setCategoryDescription(category.description || "");
      setCategoryImage(category.image || null);
    }
  }, [category]);

  const handleImagePick = async () => {
    if (Platform.OS === 'web') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = (e) => {
        const file = e.target.files[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            setCategoryImage(event.target.result);
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    }
  };

  const handleUpdateCategory = async () => {
    if (!categoryName.trim()) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message="Enter a category name!"
          />
        ),
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      const categoryData = {
        name: categoryName.trim(),
        description: categoryDescription.trim() || undefined,
        image: categoryImage || undefined,
      };

      await axios.put(
        `${API_BASE_URL}/api/categories/${category.id}`,
        categoryData,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message="Category updated successfully!"
          />
        ),
        placement: "top",
      });

      fetchCategories();
      onClose();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to update category!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Content maxWidth={isMobile ? "90%" : "400px"}>
        <Modal.CloseButton />
        <Modal.Header>
          <Text fontSize="16px" fontWeight="600" color={GlobalStyles.colors.blue}>
            Edit Category
          </Text>
        </Modal.Header>
        <Modal.Body>
          <VStack space={4} pb={4}>
            <HStack
              alignItems="center"
              space={3}
              borderWidth={1}
              borderColor="#030061"
              borderRadius={30}
              px={4}
              py={2}
            >
              <Image
                source={categoryNameIcon}
                alt="category"
                style={{ width: 28, height: 28 }}
                resizeMode="contain"
              />
              <Input
                flex={1}
                placeholder="Category Name"
                placeholderTextColor="#666666"
                fontSize="13px"
                fontWeight="400"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                variant="unstyled"
                px={0}
                value={categoryName}
                onChangeText={setCategoryName}
              />
            </HStack>

            <VStack space={2}>
              <Text fontSize="12px" color="#666">
                Description (Optional)
              </Text>
              <TextArea
                placeholder="Add description..."
                placeholderTextColor="#666666"
                fontSize="13px"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                value={categoryDescription}
                onChangeText={setCategoryDescription}
                borderColor="#030061"
                borderRadius={12}
                h={20}
                autoCompleteType={undefined}
              />
            </VStack>

            <VStack space={2}>
              <Text fontSize="12px" color="#666">
                Category Image (Optional)
              </Text>
              <Pressable onPress={handleImagePick}>
                <Box
                  borderWidth={2}
                  borderColor={GlobalStyles.colors.blue}
                  borderStyle="dashed"
                  borderRadius={8}
                  p={4}
                  alignItems="center"
                  bg={categoryImage ? "white" : "#F9FAFB"}
                  minH="120px"
                  justifyContent="center"
                >
                  {categoryImage ? (
                    <VStack space={2} alignItems="center">
                      <Image
                        source={{ uri: categoryImage }}
                        alt="Category preview"
                        style={{ width: 100, height: 100, borderRadius: 8 }}
                        resizeMode="cover"
                      />
                      <Text fontSize="11px" color={GlobalStyles.colors.blue} fontWeight="500">
                        Tap to change image
                      </Text>
                    </VStack>
                  ) : (
                    <VStack space={2} alignItems="center">
                      <Icon as={Ionicons} name="cloud-upload-outline" size="2xl" color={GlobalStyles.colors.blue} />
                      <Text fontSize="13px" color={GlobalStyles.colors.blue} fontWeight="500">
                        Tap to Upload Image
                      </Text>
                      <Text fontSize="11px" color="#999">
                        PNG, JPG up to 5MB
                      </Text>
                    </VStack>
                  )}
                </Box>
              </Pressable>
              {categoryImage && (
                <Pressable onPress={() => setCategoryImage(null)}>
                  <HStack space={1} alignItems="center" justifyContent="center">
                    <Icon as={Ionicons} name="trash-outline" size="xs" color={GlobalStyles.colors.red} />
                    <Text fontSize="12px" color={GlobalStyles.colors.red} fontWeight="500">
                      Remove Image
                    </Text>
                  </HStack>
                </Pressable>
              )}
            </VStack>

            <Pressable onPress={handleUpdateCategory} disabled={isLoading}>
              <Box
                borderRadius={8}
                overflow="hidden"
                borderWidth={1}
                borderColor={GlobalStyles.colors.blue}
                opacity={isLoading ? 0.6 : 1}
              >
                <LinearGradient
                  colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={{
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    alignItems: "center",
                  }}
                >
                  {isLoading ? (
                    <Spinner color={GlobalStyles.colors.mustard} size="sm" />
                  ) : (
                    <Text
                      fontSize="13px"
                      fontWeight="500"
                      color={GlobalStyles.colors.mustard}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      Update Category
                    </Text>
                  )}
                </LinearGradient>
              </Box>
            </Pressable>
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};

const AddBulkSlotsModal = ({ isOpen, onClose, categories, fetchCategories, toast, user }) => {
  const [selectedCategoryForSlots, setSelectedCategoryForSlots] = useState("");
  const [slotsToAdd, setSlotsToAdd] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);
  const isMobile = dimensions.width < 768;

  const handleAddSlots = async () => {
    if (!selectedCategoryForSlots) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Select a category!"}
          />
        ),
        placement: "top",
      });
      return;
    }

    if (!slotsToAdd.trim() || isNaN(slotsToAdd) || parseInt(slotsToAdd) <= 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Enter a valid number of slots!"}
          />
        ),
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/objects/bulk`,
        { 
          category_id: selectedCategoryForSlots,
          count: parseInt(slotsToAdd)
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data) {
        toast.show({
          render: () => (
            <Toast
              color={GlobalStyles.colors.green}
              message={`${slotsToAdd} slot(s) added successfully!`}
            />
          ),
          placement: "top",
        });
        
        setSelectedCategoryForSlots("");
        setSlotsToAdd("");
        fetchCategories();
        onClose();
      }
    } catch (error) {
      console.error("Error adding slots:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to add slots!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Content maxWidth={isMobile ? "90%" : "400px"}>
        <Modal.CloseButton />
        <Modal.Header>
          <Text fontSize="16px" fontWeight="600" color={GlobalStyles.colors.blue}>
            Add Bulk Slots to Category
          </Text>
        </Modal.Header>
        <Modal.Body>
          <VStack space={4} pb={4}>
            <Text fontSize="11px" color="#666" fontStyle="italic">
              💡 Add multiple slots at once to a category
            </Text>

            <HStack
              alignItems="center"
              space={3}
              borderWidth={1}
              borderColor={GlobalStyles.colors.blue}
              borderRadius={30}
              px={4}
              py={2}
            >
              <Image
                source={addObjectIcon}
                alt="slots"
                style={{ width: 28, height: 28 }}
                resizeMode="contain"
              />
              <Input
                flex={1}
                placeholder="Number of Slots"
                placeholderTextColor="#666666"
                fontSize="13px"
                fontWeight="400"
                color={GlobalStyles.colors.black}
                fontFamily={GlobalStyles.text.fontFamily}
                variant="unstyled"
                px={0}
                keyboardType="numeric"
                value={slotsToAdd}
                onChangeText={setSlotsToAdd}
              />
            </HStack>

            <VStack space={2}>
              <Text fontSize="12px" color="#666">
                Select category:
              </Text>
              {categories.length === 0 ? (
                <Text fontSize="12px" color="#999" textAlign="center" py={4}>
                  No categories available. Add a category first.
                </Text>
              ) : (
                <ScrollView maxH="200px">
                  <VStack space={2}>
                    {categories.map((cat) => (
                      <Pressable
                        key={cat.id}
                        onPress={() => setSelectedCategoryForSlots(cat.id)}
                      >
                        <Box
                          borderWidth={1}
                          borderColor={selectedCategoryForSlots === cat.id ? GlobalStyles.colors.blue : GlobalStyles.colors.blue}
                          borderRadius={8}
                          px={3}
                          py={2}
                          bg={selectedCategoryForSlots === cat.id ? "rgba(3,0,87,0.1)" : "white"}
                        >
                          <HStack justifyContent="space-between" alignItems="center">
                            <HStack space={2} flex={1} alignItems="center">
                              {cat.image && (
                                <Image
                                  source={{ uri: cat.image }}
                                  alt={cat.name}
                                  style={{ width: 30, height: 30, borderRadius: 4 }}
                                  resizeMode="cover"
                                />
                              )}
                              <Text 
                                fontSize="13px" 
                                color={selectedCategoryForSlots === cat.id ? GlobalStyles.colors.blue : "#333"}
                                fontWeight={selectedCategoryForSlots === cat.id ? "500" : "400"}
                                flex={1}
                              >
                                {cat.name}
                              </Text>
                            </HStack>
                            {cat.objects && (
                              <Text fontSize="11px" color="#666">
                                ({cat.objects.length} slots)
                              </Text>
                            )}
                          </HStack>
                          {cat.description && (
                            <Text fontSize="11px" color="#999" mt={1}>
                              {cat.description}
                            </Text>
                          )}
                        </Box>
                      </Pressable>
                    ))}
                  </VStack>
                </ScrollView>
              )}
            </VStack>

            {categories.length > 0 && (
              <Pressable onPress={handleAddSlots} disabled={isLoading}>
                <Box
                  borderRadius={8}
                  overflow="hidden"
                  borderWidth={1}
                  borderColor={GlobalStyles.colors.blue}
                  opacity={isLoading ? 0.6 : 1}
                >
                  <LinearGradient
                    colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                    start={{ x: 0.5, y: 0 }}
                    end={{ x: 0.5, y: 1 }}
                    style={{
                      paddingVertical: 10,
                      paddingHorizontal: 16,
                      alignItems: "center",
                    }}
                  >
                    {isLoading ? (
                      <Spinner color={GlobalStyles.colors.mustard} size="sm" />
                    ) : (
                      <Text
                        fontSize="13px"
                        fontWeight="500"
                        color={GlobalStyles.colors.mustard}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Add Slots
                      </Text>
                    )}
                  </LinearGradient>
                </Box>
              </Pressable>
            )}
          </VStack>
        </Modal.Body>
      </Modal.Content>
    </Modal>
  );
};
const ReservationSettingsView = ({ onHome }) => {
  const [selectedTimeUnit, setSelectedTimeUnit] = useState("hour");
  const [timeValue, setTimeValue] = useState(3);
  const [capacity, setCapacity] = useState("21");
  const [cutoffMonth, setCutoffMonth] = useState("12");
  const [cutoffDay, setCutoffDay] = useState("31");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [paymentOption, setPaymentOption] = useState("now");
  const [showCategoryScreen, setShowCategoryScreen] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  const [isLoading, setIsLoading] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showAddBulkSlotsModal, setShowAddBulkSlotsModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectionIncluded, setSelectionIncluded] = useState(true);
  const [showManageSlotsModal, setShowManageSlotsModal] = useState(false);
  
  let user = useSelector((state) => state.user.user);
  const toast = useToast();

  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchCategories();
  }, []);

  const screenWidth = dimensions.width;
  const isMobile = screenWidth < 768;

  const handleDateChange = (date) => {
    const dateObj = new Date(date);
    setCutoffMonth((dateObj.getMonth() + 1).toString());
    setCutoffDay(dateObj.getDate().toString());
    setShowDatePicker(false);
  };

  const handleTimeUnitChange = (unit) => {
    setSelectedTimeUnit(unit);
    if (unit === "day") setTimeValue(1);
    else if (unit === "hour") setTimeValue(3);
    else if (unit === "min") setTimeValue(15);
  };

  const incrementTime = () => {
    if (selectedTimeUnit === "hour") {
      setTimeValue((prev) => (prev + 1) % 24);
    } else if (selectedTimeUnit === "min") {
      setTimeValue((prev) => {
        if (prev === 15) return 30;
        if (prev === 30) return 45;
        if (prev === 45) return 60;
        return 15;
      });
    } else {
      setTimeValue((prev) => prev + 1);
    }
  };

  const decrementTime = () => {
    if (selectedTimeUnit === "hour") {
      setTimeValue((prev) => (prev === 0 ? 23 : prev - 1));
    } else if (selectedTimeUnit === "min") {
      setTimeValue((prev) => {
        if (prev === 60) return 45;
        if (prev === 45) return 30;
        if (prev === 30) return 15;
        return 60;
      });
    } else {
      setTimeValue((prev) => Math.max(0, prev - 1));
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      
      if (response.data && response.data.data) {
        const settings = response.data.data;
        if (settings.time_unit) {
          const unitMap = { 'DAY': 'day', 'HOUR': 'hour', 'MIN': 'min' };
          setSelectedTimeUnit(unitMap[settings.time_unit] || 'hour');
        }
        if (settings.capacity) setCapacity(settings.capacity.toString());
        if (settings.cut_off_month) setCutoffMonth(settings.cut_off_month.toString());
        if (settings.cut_off_day) setCutoffDay(settings.cut_off_day.toString());
        if (settings.payment_type) {
          const paymentMap = { 'NOW': 'now', 'AT_SPOT': 'at_spot' };
          setPaymentOption(paymentMap[settings.payment_type] || 'now');
        }
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });
      
      if (response.data && response.data.data) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setShowEditCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    
    if (category && category.objects && category.objects.length > 0) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={`Cannot delete category. It has ${category.objects.length} slot(s). Please remove slots first.`}
          />
        ),
        placement: "top",
      });
      
      setSelectedCategory(category);
      setShowManageSlotsModal(true);
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/api/categories/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Category deleted successfully!"}
          />
        ),
        placement: "top",
      });
      
      fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to delete category!"}
          />
        ),
        placement: "top",
      });
    }
  };

  const handleContinue = async () => {
    setIsLoading(true);
    try {
      const timeUnitMap = { 'day': 'DAY', 'hour': 'HOUR', 'min': 'MIN' };
      const paymentTypeMap = { 'now': 'NOW', 'at_spot': 'AT_SPOT' };
      
      const settingsData = {
        time_unit: timeUnitMap[selectedTimeUnit],
        capacity: parseInt(capacity),
        cut_off_month: parseInt(cutoffMonth),
        cut_off_day: parseInt(cutoffDay),
        payment_type: paymentTypeMap[paymentOption],
      };

      await axios.put(
        `${API_BASE_URL}/api/settings`,
        settingsData,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Settings saved successfully!"}
          />
        ),
        placement: "top",
      });

      setShowCategoryScreen(true);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to save settings!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };
  if (showCategoryScreen) {
    return (
      <>
        <ScrollView w="100%">
          <VStack w="100%" alignItems="center" space={6} mt={1} pb={8}>
            <HStack w="100%" justifyContent="flex-end" px={4} mt={2}>
              <HomeButton onPress={onHome} />
            </HStack>

            <VStack w="100%" maxW="550px" alignItems="center" space={5} px={4}>
              <VStack w="100%" maxW="450px" alignItems="center" space={4}>
                <Text
                  fontSize="17px"
                  fontWeight="400"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Selection Included
                </Text>

                <HStack space={3} flexWrap="wrap" justifyContent="center">
                  <Pressable onPress={() => setSelectionIncluded(true)}>
                    <Box
                      borderWidth={1}
                      borderColor={GlobalStyles.colors.blue}
                      borderRadius={0}
                      overflow="hidden"
                    >
                      <LinearGradient
                        colors={
                          selectionIncluded
                            ? [GlobalStyles.colors.blue, "#0042C7"]
                            : [GlobalStyles.colors.white, "#999999"]
                        }
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={{
                          paddingVertical: isMobile ? 6 : 8,
                          paddingHorizontal: isMobile ? 24 : 32,
                          minWidth: isMobile ? 70 : 90,
                          alignItems: "center",
                        }}
                      >
                        <Text
                          fontSize={isMobile ? "13px" : "15px"}
                          fontWeight="500"
                          color={
                            selectionIncluded
                              ? GlobalStyles.colors.white
                              : GlobalStyles.colors.black
                          }
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          Yes
                        </Text>
                      </LinearGradient>
                    </Box>
                  </Pressable>

                  <Pressable onPress={() => setSelectionIncluded(false)}>
                    <Box
                      borderWidth={1}
                      borderColor={GlobalStyles.colors.blue}
                      borderRadius={0}
                      overflow="hidden"
                    >
                      <LinearGradient
                        colors={
                          !selectionIncluded
                            ? [GlobalStyles.colors.blue, "#0042C7"]
                            : [GlobalStyles.colors.white, "#999999"]
                        }
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={{
                          paddingVertical: isMobile ? 6 : 8,
                          paddingHorizontal: isMobile ? 24 : 32,
                          minWidth: isMobile ? 70 : 90,
                          alignItems: "center",
                        }}
                      >
                        <Text
                          fontSize={isMobile ? "13px" : "15px"}
                          fontWeight="500"
                          color={
                            !selectionIncluded
                              ? GlobalStyles.colors.white
                              : GlobalStyles.colors.black
                          }
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          No
                        </Text>
                      </LinearGradient>
                    </Box>
                  </Pressable>
                </HStack>

                {selectionIncluded && (
                  <Box
                    bg={GlobalStyles.colors.white}
                    borderRadius={16}
                    w="100%"
                    maxW={isMobile ? "350px" : "420px"}
                    px={isMobile ? 4 : 6}
                    py={isMobile ? 3 : 4}
                    mt={2}
                    style={
                      Platform.OS === "web"
                        ? { boxShadow: "0px 4px 4px 0px #00000040" }
                        : {
                          shadowColor: GlobalStyles.colors.black,
                          shadowOffset: { width: 0, height: 4 },
                          shadowOpacity: 0.25,
                          shadowRadius: 4,
                          elevation: 4,
                        }
                    }
                  >
                    <VStack space={3} alignItems="flex-start">
                      <Pressable onPress={() => setShowAddCategoryModal(true)} w="100%">
                        <HStack
                          alignItems="center"
                          space={3}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.blue}
                          borderRadius={30}
                          px={4}
                          py={2}
                          w="100%"
                          maxW={isMobile ? "280px" : "300px"}
                        >
                          <Image
                            source={categoryNameIcon}
                            alt="category"
                            style={{ width: 28, height: 28 }}
                            resizeMode="contain"
                          />
                          <Text
                            flex={1}
                            fontSize="13px"
                            color="#666666"
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            Add Category Name
                          </Text>
                        </HStack>
                      </Pressable>

                      <Pressable onPress={() => setShowAddCategoryModal(true)} w="100%">
                        <HStack
                          alignItems="center"
                          space={3}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.blue}
                          borderRadius={30}
                          px={4}
                          py={2}
                          w="100%"
                          maxW={isMobile ? "280px" : "300px"}
                        >
                          <Image
                            source={otherCategoryIcon}
                            alt="other"
                            style={{ width: 28, height: 28 }}
                            resizeMode="contain"
                          />
                          <Text
                            flex={1}
                            fontSize="13px"
                            color="#666666"
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            Add Other Category
                          </Text>
                        </HStack>
                      </Pressable>

                      <Pressable onPress={() => setShowAddBulkSlotsModal(true)} w="100%">
                        <HStack
                          alignItems="center"
                          space={3}
                          borderWidth={1}
                          borderColor={GlobalStyles.colors.blue}
                          borderRadius={30}
                          px={4}
                          py={2}
                          w="100%"
                          maxW={isMobile ? "220px" : "240px"}
                        >
                          <Image
                            source={addObjectIcon}
                            alt="objects"
                            style={{ width: 28, height: 28 }}
                            resizeMode="contain"
                          />
                          <Text
                            flex={1}
                            fontSize="13px"
                            color="#666666"
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            Add Slots to:
                          </Text>
                        </HStack>
                      </Pressable>
                    </VStack>
                  </Box>
                )}
              </VStack>
              {categories.length > 0 && (
                <VStack w="100%" space={3} mt={3}>
                  <Text fontSize="14px" fontWeight="500" color={GlobalStyles.colors.blue}>
                    Current Categories
                  </Text>
                  {categories.map((cat) => (
                    <Box
                      key={cat.id}
                      borderWidth={1}
                      borderColor="#E0E0E0"
                      borderRadius={8}
                      p={3}
                      bg="white"
                    >
                      <HStack justifyContent="space-between" alignItems="center">
                        <HStack space={2} flex={1} alignItems="center">
                          {cat.image && (
                            <Image
                              source={{ uri: cat.image }}
                              alt={cat.name}
                              style={{ width: 50, height: 50, borderRadius: 8 }}
                              resizeMode="cover"
                            />
                          )}
                          <VStack flex={1}>
                            <Text fontSize="14px" fontWeight="500" color={GlobalStyles.colors.blue}>
                              {cat.name}
                            </Text>
                            {cat.description && (
                              <Text fontSize="12px" color="#666" mt={1}>
                                {cat.description}
                              </Text>
                            )}
                            <Text fontSize="11px" color="#999" mt={1}>
                              {cat.objects?.length || 0} slots
                            </Text>
                          </VStack>
                        </HStack>
                        <HStack space={3} alignItems="center">
                          <Pressable onPress={() => handleEditCategory(cat)}>
                            <Text
                              fontSize="13px"
                              color={GlobalStyles.colors.blue}
                              fontWeight="500"
                              textDecorationLine="underline"
                            >
                              Edit
                            </Text>
                          </Pressable>
                          
                          <Pressable onPress={() => {
                            setSelectedCategory(cat);
                            setShowManageSlotsModal(true);
                          }}>
                            <Icon 
                              as={Ionicons} 
                              name="settings-outline" 
                              size="md" 
                              color={GlobalStyles.colors.blue} 
                            />
                          </Pressable>
                          
                          <Pressable onPress={() => handleDeleteCategory(cat.id)}>
                            <Icon 
                              as={Ionicons} 
                              name="trash-outline" 
                              size="md" 
                              color={GlobalStyles.colors.mustard} 
                            />
                          </Pressable>
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </VStack>
          </VStack>
        </ScrollView>

        <AddCategoryModal
          isOpen={showAddCategoryModal}
          onClose={() => setShowAddCategoryModal(false)}
          fetchCategories={fetchCategories}
          toast={toast}
          user={user}
        />

        <EditCategoryModal
          isOpen={showEditCategoryModal}
          onClose={() => {
            setShowEditCategoryModal(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          fetchCategories={fetchCategories}
          toast={toast}
          user={user}
        />

        <AddBulkSlotsModal
          isOpen={showAddBulkSlotsModal}
          onClose={() => setShowAddBulkSlotsModal(false)}
          categories={categories}
          fetchCategories={fetchCategories}
          toast={toast}
          user={user}
        />

        <ManageCategorySlotsModal
          isOpen={showManageSlotsModal}
          onClose={() => {
            setShowManageSlotsModal(false);
            setSelectedCategory(null);
          }}
          category={selectedCategory}
          fetchCategories={fetchCategories}
          toast={toast}
          user={user}
        />
      </>
    );
  }
  return (
    <ScrollView w="100%">
      <VStack w="100%" alignItems="center" space={4} mt={1} pb={4}>
        <HStack w="100%" justifyContent="flex-end" px={2} mt={2}>
          <HomeButton onPress={onHome} />
        </HStack>

        <VStack w="100%" maxW="500px" alignItems="center" space={3} px={2}>
          <Text
            fontSize="17px"
            fontWeight="400"
            color={GlobalStyles.colors.black}
            fontFamily={GlobalStyles.text.fontFamily}
          >
            Time Unit
          </Text>

          <Text fontSize="11px" color="#666" textAlign="center" fontStyle="italic" px={4}>
            Customer can do reservation for a day, for hours or for minutes.
          </Text>

          <HStack space={3} justifyContent="center" alignItems="flex-start">
            <Pressable onPress={() => handleTimeUnitChange("day")}>
              <VStack alignItems="center" space={1.5}>
                <Text
                  fontSize="12px"
                  fontWeight={selectedTimeUnit === "day" ? "500" : "300"}
                  color={selectedTimeUnit === "day" ? GlobalStyles.colors.blue : GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Day
                </Text>
                <Box
                  borderWidth={2}
                  borderColor={selectedTimeUnit === "day" ? GlobalStyles.colors.blue : "#CCCCCC"}
                  w="60px"
                  h="60px"
                  alignItems="center"
                  justifyContent="center"
                  bg={selectedTimeUnit === "day" ? "rgba(3,0,87,0.05)" : "white"}
                >
                  <Text
                    fontSize="15px"
                    fontWeight="400"
                    color={selectedTimeUnit === "day" ? GlobalStyles.colors.blue : "#999"}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {selectedTimeUnit === "day" ? timeValue : "-"}
                  </Text>
                </Box>
              </VStack>
            </Pressable>

            <Pressable onPress={() => handleTimeUnitChange("hour")}>
              <VStack alignItems="center" space={1.5}>
                <Text
                  fontSize="12px"
                  fontWeight={selectedTimeUnit === "hour" ? "500" : "300"}
                  color={selectedTimeUnit === "hour" ? GlobalStyles.colors.blue : GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Hours
                </Text>
                <Box
                  borderWidth={2}
                  borderColor={selectedTimeUnit === "hour" ? GlobalStyles.colors.blue : "#CCCCCC"}
                  w="60px"
                  h="60px"
                  alignItems="center"
                  justifyContent="center"
                  bg={selectedTimeUnit === "hour" ? "rgba(3,0,87,0.05)" : "white"}
                >
                  <Text
                    fontSize="15px"
                    fontWeight="400"
                    color={selectedTimeUnit === "hour" ? GlobalStyles.colors.blue : "#999"}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {selectedTimeUnit === "hour" ? timeValue : "-"}
                  </Text>
                </Box>
              </VStack>
            </Pressable>

            <Pressable onPress={() => handleTimeUnitChange("min")}>
              <VStack alignItems="center" space={1.5}>
                <Text
                  fontSize="12px"
                  fontWeight={selectedTimeUnit === "min" ? "500" : "300"}
                  color={selectedTimeUnit === "min" ? GlobalStyles.colors.blue : GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Minutes
                </Text>
                <Box
                  borderWidth={2}
                  borderColor={selectedTimeUnit === "min" ? GlobalStyles.colors.blue : "#CCCCCC"}
                  w="60px"
                  h="60px"
                  alignItems="center"
                  justifyContent="center"
                  bg={selectedTimeUnit === "min" ? "rgba(3,0,87,0.05)" : "white"}
                >
                  <Text
                    fontSize="15px"
                    fontWeight="400"
                    color={selectedTimeUnit === "min" ? GlobalStyles.colors.blue : "#999"}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    {selectedTimeUnit === "min" ? timeValue : "-"}
                  </Text>
                </Box>
              </VStack>
            </Pressable>
          </HStack>

          <VStack alignItems="center" space={1.5}>
            <Pressable onPress={incrementTime} hitSlop={8}>
              <Image
                source={topArrowIcon}
                alt="increase"
                style={{ width: 32, height: 16 }}
                resizeMode="contain"
              />
            </Pressable>
            <Pressable onPress={decrementTime} hitSlop={8}>
              <Image
                source={bottomArrowIcon}
                alt="decrease"
                style={{ width: 32, height: 16 }}
                resizeMode="contain"
              />
            </Pressable>
          </VStack>

          <Box
            w="100%"
            maxW={isMobile ? "320px" : "380px"}
            mt={2}
            borderRadius={12}
            overflow="hidden"
            borderWidth={1}
            borderColor={GlobalStyles.colors.black}
            style={
              Platform.OS === "web"
                ? { boxShadow: "0px 4px 4px 0px #00000040" }
                : {
                    shadowColor: GlobalStyles.colors.black,
                    shadowOffset: { width: 0, height: 4 },
                    shadowOpacity: 0.25,
                    shadowRadius: 4,
                    elevation: 4,
                  }
            }
          >
            <LinearGradient
              colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
              start={{ x: 0.5, y: 0 }}
              end={{ x: 0.5, y: 1 }}
              style={{
                paddingVertical: isMobile ? 12 : 14,
                paddingHorizontal: isMobile ? 14 : 16,
              }}
            >
              <VStack space={2.5}>
                <HStack justifyContent="space-between" alignItems="center">
                  <Text
                    fontSize="13px"
                    fontWeight="400"
                    color={GlobalStyles.colors.white}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Capacity
                  </Text>
                  <Input
                    value={capacity}
                    onChangeText={(text) => {
                      // Only allow numbers
                      const numericValue = text.replace(/[^0-9]/g, '');
                      setCapacity(numericValue);
                    }}
                    w="70px"
                    h="32px"
                    textAlign="center"
                    fontSize="14px"
                    fontWeight="400"
                    color={GlobalStyles.colors.mustard}
                    fontFamily={GlobalStyles.text.fontFamily}
                    bg="rgba(255,255,255,0.2)"
                    borderRadius={8}
                    borderColor="transparent"
                    variant="unstyled"
                    px={2}
                    type="text"
                    _focus={{
                      bg: "rgba(255,255,255,0.3)",
                      borderColor: GlobalStyles.colors.mustard,
                    }}
                  />
                </HStack>

                <Text fontSize="9px" color="rgba(255,255,255,0.8)" fontStyle="italic">
                  Max Reservation to be made
                </Text>

                <HStack justifyContent="space-between" alignItems="center">
                  <Text
                    fontSize="13px"
                    fontWeight="400"
                    color={GlobalStyles.colors.white}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Cut off Date
                  </Text>
                  <Pressable onPress={() => setShowDatePicker(!showDatePicker)}>
                    <Box
                      borderRadius={12}
                      overflow="hidden"
                      borderWidth={1}
                      borderColor={GlobalStyles.colors.black}
                    >
                      <LinearGradient
                        colors={[
                          GlobalStyles.colors.gradientBlueStart,
                          "#007AFF",
                        ]}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        style={{
                          paddingVertical: 6,
                          paddingHorizontal: 14,
                          minWidth: 65,
                          alignItems: "center",
                        }}
                      >
                        <Text
                          fontSize="14px"
                          fontWeight="400"
                          color={GlobalStyles.colors.mustard}
                          fontFamily={GlobalStyles.text.fontFamily}
                        >
                          {cutoffMonth}/{cutoffDay}
                        </Text>
                      </LinearGradient>
                    </Box>
                  </Pressable>
                </HStack>

                <Text fontSize="9px" color="rgba(255,255,255,0.8)" fontStyle="italic">
                  Customer can not make Reservation after this date
                </Text>

                {showDatePicker && (
                  <Box mt={1}>
                    <DatePickerSingleInput onChange={handleDateChange} />
                  </Box>
                )}

                <HStack
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Text
                    fontSize="13px"
                    fontWeight="400"
                    color={GlobalStyles.colors.white}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Payment
                  </Text>
                  <HStack space={2}>
                    <Pressable onPress={() => setPaymentOption("now")}>
                      <Box
                        borderRadius={12}
                        overflow="hidden"
                        borderWidth={1}
                        borderColor={GlobalStyles.colors.black}
                      >
                        <LinearGradient
                          colors={
                            paymentOption === "now"
                              ? [
                                  GlobalStyles.colors.gradientBlueStart,
                                  "#007AFF",
                                ]
                              : [
                                  GlobalStyles.colors.white,
                                  GlobalStyles.colors.white,
                                ]
                          }
                          start={{ x: 0.5, y: 0 }}
                          end={{ x: 0.5, y: 1 }}
                          style={{
                            paddingVertical: 6,
                            paddingHorizontal: 16,
                            minWidth: 65,
                            alignItems: "center",
                          }}
                        >
                          <Text
                            fontSize="13px"
                            fontWeight="400"
                            color={
                              paymentOption === "now"
                                ? GlobalStyles.colors.mustard
                                : GlobalStyles.colors.blue
                            }
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            Now
                          </Text>
                        </LinearGradient>
                      </Box>
                    </Pressable>
                    <Pressable onPress={() => setPaymentOption("at_spot")}>
                      <Box
                        borderRadius={12}
                        overflow="hidden"
                        borderWidth={1}
                        borderColor={GlobalStyles.colors.black}
                      >
                        <LinearGradient
                          colors={
                            paymentOption === "at_spot"
                              ? [
                                  GlobalStyles.colors.gradientBlueStart,
                                  "#007AFF",
                                ]
                              : [
                                  GlobalStyles.colors.white,
                                  GlobalStyles.colors.white,
                                ]
                          }
                          start={{ x: 0.5, y: 0 }}
                          end={{ x: 0.5, y: 1 }}
                          style={{
                            paddingVertical: 6,
                            paddingHorizontal: 16,
                            minWidth: 65,
                            alignItems: "center",
                          }}
                        >
                          <Text
                            fontSize="13px"
                            fontWeight="400"
                            color={
                              paymentOption === "at_spot"
                                ? GlobalStyles.colors.mustard
                                : GlobalStyles.colors.blue
                            }
                            fontFamily={GlobalStyles.text.fontFamily}
                          >
                            At spot
                          </Text>
                        </LinearGradient>
                      </Box>
                    </Pressable>
                  </HStack>
                </HStack>

                <Text fontSize="9px" color="rgba(255,255,255,0.8)" fontStyle="italic">
                  Customer pays at time of reservation (now) or at the spot.
                </Text>
              </VStack>
            </LinearGradient>
          </Box>

          <Pressable onPress={handleContinue} mt={3} disabled={isLoading}>
            <Box
              borderRadius={12}
              overflow="hidden"
              borderWidth={1}
              borderColor={GlobalStyles.colors.black}
              opacity={isLoading ? 0.6 : 1}
            >
              <LinearGradient
                colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 32,
                  alignItems: "center",
                }}
              >
                {isLoading ? (
                  <Spinner color={GlobalStyles.colors.mustard} size="sm" />
                ) : (
                  <Text
                    fontSize="14px"
                    fontWeight="400"
                    color={GlobalStyles.colors.mustard}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Continue
                  </Text>
                )}
              </LinearGradient>
            </Box>
          </Pressable>
        </VStack>
      </VStack>
    </ScrollView>
  );
}; 


const MakeReservationView = ({ onHome }) => {
  const [businessCategory, setBusinessCategory] = useState("");
  const [totalAvailableSlots, setTotalAvailableSlots] = useState("");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  const [customerName, setCustomerName] = useState("");
  const [reservationDate, setReservationDate] = useState("");
  const [reservationTime, setReservationTime] = useState("");
  const [seatNumber, setSeatNumber] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);

  const [paymentType, setPaymentType] = useState("AT_SPOT");

  const [cutOffMonth, setCutOffMonth] = useState("");
  const [cutOffDay, setCutOffDay] = useState("");

  const [loading, setLoading] = useState(false);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));
  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);
  const isMobile = dimensions.width < 768;

  const toast = useToast();
  let user = useSelector((state) => state.user.user);

  const categoryOptions = ["Restaurant", "Hotel", "Salon", "Dental"];

  const cardShadowStyle =
    Platform.OS === "web"
      ? { boxShadow: "0px 3px 10px 0px rgba(3,0,87,0.08)" }
      : {
          shadowColor: GlobalStyles.colors.black,
          shadowOffset: { width: 0, height: 3 },
          shadowOpacity: 0.12,
          shadowRadius: 8,
          elevation: 3,
        };

  const handleDateChange = (date) => {
    const dateObj = new Date(date);
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    setReservationDate(`${yyyy}-${mm}-${dd}`);
    setShowDatePicker(false);
  };

  const resetForm = () => {
    setBusinessCategory("");
    setTotalAvailableSlots("");
    setCustomerName("");
    setReservationDate("");
    setReservationTime("");
    setSeatNumber("");
    setPaymentType("AT_SPOT");
    setCutOffMonth("");
    setCutOffDay("");
  };

  const validateForm = () => {
    if (!businessCategory || !categoryOptions.includes(businessCategory)) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Please select a valid business category!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    if (
      !totalAvailableSlots.toString().trim() ||
      isNaN(totalAvailableSlots) ||
      parseInt(totalAvailableSlots) < 0
    ) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Enter a valid total available slots!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    if (!reservationDate || !/^\d{4}-\d{2}-\d{2}$/.test(reservationDate)) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Please select a valid reservation date!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    if (!reservationTime || !/^\d{2}:\d{2}:\d{2}$/.test(reservationTime)) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Enter a valid time in HH:mm:ss format!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    if (!["NOW", "AT_SPOT"].includes(paymentType)) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Please select a payment type!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    const monthNum = parseInt(cutOffMonth);
    if (!cutOffMonth || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Cut off month must be between 1 and 12!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    const dayNum = parseInt(cutOffDay);
    if (!cutOffDay || isNaN(dayNum) || dayNum < 1 || dayNum > 31) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={"Cut off day must be between 1 and 31!"}
          />
        ),
        placement: "top",
      });
      return false;
    }

    return true;
  };

  const createBusinessReservation = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE_URL}/api/reservations/business/make`,
        {
          total_available_slots: parseInt(totalAvailableSlots),
          business_category: businessCategory,
          customer_name: customerName ? customerName.trim() : undefined,
          date: reservationDate,
          time: reservationTime,
          seat_no: seatNumber,
          payment_type: paymentType,
          cut_off_month: parseInt(cutOffMonth),
          cut_off_day: parseInt(cutOffDay),
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.green}
            message={"Reservation created successfully."}
          />
        ),
        placement: "top",
      });

      resetForm();
      onHome();
    } catch (error) {
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={error.response?.data?.message || "Failed to create reservation!"}
          />
        ),
        placement: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView w="100%">
      <VStack w="100%" alignItems="center" space={3.5} mt={1} pb={8}>
        <HStack w="100%" justifyContent="flex-end" px={2} mt={2}>
          <HomeButton onPress={onHome} />
        </HStack>

        <VStack w="100%" maxW="460px" alignItems="center" space={4} px={2}>
          {/* Section 1: Business Information */}
          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={14}
            w="100%"
            maxW={isMobile ? "330px" : "390px"}
            px={isMobile ? 3.5 : 5}
            py={isMobile ? 2.5 : 3.5}
            position="relative"
            zIndex={showCategoryDropdown ? 50 : 2}
            style={cardShadowStyle}
          >
            <VStack space={2.5}>
              <Text
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.3px"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                BUSINESS INFORMATION
              </Text>

              <Box position="relative" zIndex={showCategoryDropdown ? 999 : 1}>
                <Pressable onPress={() => setShowCategoryDropdown(!showCategoryDropdown)}>
                  <HStack
                    alignItems="center"
                    justifyContent="space-between"
                    space={2}
                    borderWidth={1}
                    borderColor={
                      showCategoryDropdown ? GlobalStyles.colors.blue : "#DADCE6"
                    }
                    borderRadius={24}
                    px={3.5}
                    py={1.5}
                    bg={GlobalStyles.colors.white}
                  >
                    <Text
                      fontSize="12px"
                      color={businessCategory ? GlobalStyles.colors.black : "#8A8A9A"}
                      fontFamily={GlobalStyles.text.fontFamily}
                    >
                      {businessCategory || "Select Business Category"}
                    </Text>
                    <Icon
                      as={Ionicons}
                      name={showCategoryDropdown ? "chevron-up" : "chevron-down"}
                      size="xs"
                      color={GlobalStyles.colors.blue}
                    />
                  </HStack>
                </Pressable>

                {showCategoryDropdown && (
                  <Box
                    position="absolute"
                    top="105%"
                    left={0}
                    right={0}
                    mt={1}
                    bg={GlobalStyles.colors.white}
                    borderRadius={12}
                    borderWidth={1}
                    borderColor="#E4E6F0"
                    zIndex={999}
                    overflow="hidden"
                    style={
                      Platform.OS === "web"
                        ? { boxShadow: "0px 6px 16px 0px rgba(3,0,87,0.16)" }
                        : {
                            shadowColor: GlobalStyles.colors.black,
                            shadowOffset: { width: 0, height: 4 },
                            shadowOpacity: 0.18,
                            shadowRadius: 10,
                            elevation: 8,
                          }
                    }
                  >
                    {categoryOptions.map((option, index) => {
                      const isSelected = businessCategory === option;
                      return (
                        <Pressable
                          key={option}
                          onPress={() => {
                            setBusinessCategory(option);
                            setShowCategoryDropdown(false);
                          }}
                        >
                          <HStack
                            justifyContent="space-between"
                            alignItems="center"
                            px={3.5}
                            py={2}
                            borderBottomWidth={index < categoryOptions.length - 1 ? 1 : 0}
                            borderBottomColor="#F0F1F7"
                            bg={isSelected ? "rgba(3,0,87,0.05)" : GlobalStyles.colors.white}
                          >
                            <Text
                              fontSize="12px"
                              fontWeight={isSelected ? "500" : "400"}
                              color={
                                isSelected ? GlobalStyles.colors.blue : GlobalStyles.colors.black
                              }
                              fontFamily={GlobalStyles.text.fontFamily}
                            >
                              {option}
                            </Text>
                            {isSelected && (
                              <Icon
                                as={Ionicons}
                                name="checkmark"
                                size="xs"
                                color={GlobalStyles.colors.blue}
                              />
                            )}
                          </HStack>
                        </Pressable>
                      );
                    })}
                  </Box>
                )}
              </Box>

              <HStack
                alignItems="center"
                space={2}
                borderWidth={1}
                borderColor="#DADCE6"
                borderRadius={24}
                px={3.5}
                py={1.5}
              >
                <Input
                  flex={1}
                  variant="unstyled"
                  placeholder="Total Available Slots"
                  placeholderTextColor="#8A8A9A"
                  fontSize="12px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  keyboardType="numeric"
                  value={totalAvailableSlots}
                  onChangeText={(text) => {
                    const numericValue = text.replace(/[^0-9]/g, "");
                    setTotalAvailableSlots(numericValue);
                  }}
                />
              </HStack>
            </VStack>
          </Box>

          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={14}
            w="100%"
            maxW={isMobile ? "330px" : "390px"}
            px={isMobile ? 3.5 : 5}
            py={isMobile ? 2.5 : 3.5}
            zIndex={1}
            style={cardShadowStyle}
          >
            <VStack space={2.5}>
              <Text
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.3px"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                CUSTOMER
              </Text>

              <HStack
                alignItems="center"
                space={2}
                borderWidth={1}
                borderColor="#DADCE6"
                borderRadius={24}
                px={3.5}
                py={1.5}
              >
                <Input
                  flex={1}
                  variant="unstyled"
                  placeholder="Customer Name (Optional)"
                  placeholderTextColor="#8A8A9A"
                  fontSize="12px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  value={customerName}
                  onChangeText={setCustomerName}
                />
              </HStack>
            </VStack>
          </Box>
          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={14}
            w="100%"
            maxW={isMobile ? "330px" : "390px"}
            px={isMobile ? 3.5 : 5}
            py={isMobile ? 2.5 : 3.5}
            zIndex={1}
            style={cardShadowStyle}
          >
            <VStack space={2.5}>
              <Text
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.3px"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                RESERVATION
              </Text>

              <HStack justifyContent="space-between" alignItems="center">
                <Text
                  fontSize="12px"
                  fontWeight="400"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                >
                  Reservation Date
                </Text>
                <Pressable onPress={() => setShowDatePicker(!showDatePicker)}>
                  <Box
                    borderRadius={10}
                    overflow="hidden"
                    borderWidth={1}
                    borderColor={GlobalStyles.colors.black}
                  >
                    <LinearGradient
                      colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                      start={{ x: 0.5, y: 0 }}
                      end={{ x: 0.5, y: 1 }}
                      style={{
                        paddingVertical: 5,
                        paddingHorizontal: 12,
                        minWidth: 80,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        fontSize="12px"
                        fontWeight="400"
                        color={GlobalStyles.colors.mustard}
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        {reservationDate || "Select Date"}
                      </Text>
                    </LinearGradient>
                  </Box>
                </Pressable>
              </HStack>

              {showDatePicker && (
                <Box mt={1}>
                  <DatePickerSingleInput onChange={handleDateChange} />
                </Box>
              )}

              <HStack
                alignItems="center"
                space={2}
                borderWidth={1}
                borderColor="#DADCE6"
                borderRadius={24}
                px={3.5}
                py={1.5}
              >
                <Input
                  flex={1}
                  variant="unstyled"
                  placeholder="Reservation Time (HH:mm:ss)"
                  placeholderTextColor="#8A8A9A"
                  fontSize="12px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  value={reservationTime}
                  onChangeText={setReservationTime}
                />
              </HStack>

              <HStack
                alignItems="center"
                space={2}
                borderWidth={1}
                borderColor="#DADCE6"
                borderRadius={24}
                px={3.5}
                py={1.5}
              >
                <Input
                  flex={1}
                  variant="unstyled"
                  placeholder="Seat Number (e.g. A12)"
                  placeholderTextColor="#8A8A9A"
                  fontSize="12px"
                  color={GlobalStyles.colors.black}
                  fontFamily={GlobalStyles.text.fontFamily}
                  value={seatNumber}
                  onChangeText={setSeatNumber}
                />
              </HStack>
            </VStack>
          </Box>

          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={14}
            w="100%"
            maxW={isMobile ? "330px" : "390px"}
            px={isMobile ? 3.5 : 5}
            py={isMobile ? 2.5 : 3.5}
            zIndex={1}
            style={cardShadowStyle}
          >
            <VStack space={2.5}>
              <Text
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.3px"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                PAYMENT
              </Text>

              <HStack space={2} justifyContent="flex-start">
                <Pressable onPress={() => setPaymentType("NOW")}>
                  <Box
                    borderRadius={10}
                    overflow="hidden"
                    borderWidth={1}
                    borderColor={GlobalStyles.colors.black}
                  >
                    <LinearGradient
                      colors={
                        paymentType === "NOW"
                          ? [GlobalStyles.colors.gradientBlueStart, "#007AFF"]
                          : [GlobalStyles.colors.white, GlobalStyles.colors.white]
                      }
                      start={{ x: 0.5, y: 0 }}
                      end={{ x: 0.5, y: 1 }}
                      style={{
                        paddingVertical: 5,
                        paddingHorizontal: 14,
                        minWidth: 58,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        fontSize="12px"
                        fontWeight="400"
                        color={
                          paymentType === "NOW"
                            ? GlobalStyles.colors.mustard
                            : GlobalStyles.colors.blue
                        }
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        Now
                      </Text>
                    </LinearGradient>
                  </Box>
                </Pressable>

                <Pressable onPress={() => setPaymentType("AT_SPOT")}>
                  <Box
                    borderRadius={10}
                    overflow="hidden"
                    borderWidth={1}
                    borderColor={GlobalStyles.colors.black}
                  >
                    <LinearGradient
                      colors={
                        paymentType === "AT_SPOT"
                          ? [GlobalStyles.colors.gradientBlueStart, "#007AFF"]
                          : [GlobalStyles.colors.white, GlobalStyles.colors.white]
                      }
                      start={{ x: 0.5, y: 0 }}
                      end={{ x: 0.5, y: 1 }}
                      style={{
                        paddingVertical: 5,
                        paddingHorizontal: 14,
                        minWidth: 58,
                        alignItems: "center",
                      }}
                    >
                      <Text
                        fontSize="12px"
                        fontWeight="400"
                        color={
                          paymentType === "AT_SPOT"
                            ? GlobalStyles.colors.mustard
                            : GlobalStyles.colors.blue
                        }
                        fontFamily={GlobalStyles.text.fontFamily}
                      >
                        At spot
                      </Text>
                    </LinearGradient>
                  </Box>
                </Pressable>
              </HStack>
            </VStack>
          </Box>

          <Box
            bg={GlobalStyles.colors.white}
            borderRadius={14}
            w="100%"
            maxW={isMobile ? "330px" : "390px"}
            px={isMobile ? 3.5 : 5}
            py={isMobile ? 2.5 : 3.5}
            zIndex={1}
            style={cardShadowStyle}
          >
            <VStack space={2.5}>
              <Text
                fontSize="12px"
                fontWeight="600"
                letterSpacing="0.3px"
                color={GlobalStyles.colors.blue}
                fontFamily={GlobalStyles.text.fontFamily}
              >
                RESERVATION RULES
              </Text>

              <HStack space={2.5}>
                <HStack
                  flex={1}
                  alignItems="center"
                  space={2}
                  borderWidth={1}
                  borderColor="#DADCE6"
                  borderRadius={24}
                  px={3.5}
                  py={1.5}
                >
                  <Input
                    flex={1}
                    variant="unstyled"
                    placeholder="Cut Off Month"
                    placeholderTextColor="#8A8A9A"
                    fontSize="12px"
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                    keyboardType="numeric"
                    value={cutOffMonth}
                    onChangeText={(text) => {
                      const numericValue = text.replace(/[^0-9]/g, "");
                      setCutOffMonth(numericValue);
                    }}
                  />
                </HStack>

                <HStack
                  flex={1}
                  alignItems="center"
                  space={2}
                  borderWidth={1}
                  borderColor="#DADCE6"
                  borderRadius={24}
                  px={3.5}
                  py={1.5}
                >
                  <Input
                    flex={1}
                    variant="unstyled"
                    placeholder="Cut Off Day"
                    placeholderTextColor="#8A8A9A"
                    fontSize="12px"
                    color={GlobalStyles.colors.black}
                    fontFamily={GlobalStyles.text.fontFamily}
                    keyboardType="numeric"
                    value={cutOffDay}
                    onChangeText={(text) => {
                      const numericValue = text.replace(/[^0-9]/g, "");
                      setCutOffDay(numericValue);
                    }}
                  />
                </HStack>
              </HStack>
            </VStack>
          </Box>

          {/* Bottom Button */}
          <Pressable onPress={createBusinessReservation} mt={0.5} disabled={loading}>
            <Box
              borderRadius={10}
              overflow="hidden"
              borderWidth={1}
              borderColor={GlobalStyles.colors.black}
              opacity={loading ? 0.6 : 1}
            >
              <LinearGradient
                colors={[GlobalStyles.colors.gradientBlueStart, "#007AFF"]}
                start={{ x: 0.5, y: 0 }}
                end={{ x: 0.5, y: 1 }}
                style={{
                  paddingVertical: 9,
                  paddingHorizontal: 28,
                  alignItems: "center",
                }}
              >
                {loading ? (
                  <Spinner color={GlobalStyles.colors.mustard} size="sm" />
                ) : (
                  <Text
                    fontSize="13px"
                    fontWeight="500"
                    color={GlobalStyles.colors.mustard}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    Create Reservation
                  </Text>
                )}
              </LinearGradient>
            </Box>
          </Pressable>
        </VStack>
      </VStack>
    </ScrollView>
  );
};

const StatCardDropdown = ({
  selectedValue,
  onValueChange,
  options,
  cardIndex,
  isOpen,
  setIsOpen,
}) => {
  const handlePress = () => {
    setIsOpen(isOpen ? null : cardIndex);
  };

  return (
    <Box position="relative" zIndex={isOpen ? 999 : 1}>
      <Pressable onPress={handlePress}>
        <HStack
          alignItems="center"
          space={1}
          borderWidth={1}
          borderColor="rgba(255,255,255,0.3)"
          borderRadius={6}
          px={2}
          py={1}
        >
          <Text
            fontSize="10px"
            fontWeight="400"
            color={GlobalStyles.colors.white}
          >
            {selectedValue}
          </Text>
          <Icon
            as={Ionicons}
            name={isOpen ? "chevron-up" : "chevron-down"}
            color={GlobalStyles.colors.white}
            size="xs"
          />
        </HStack>
      </Pressable>

      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          right={0}
          mt={1}
          bg={GlobalStyles.colors.white}
          borderRadius={6}
          borderWidth={1}
          borderColor="#ccc"
          minW="90px"
          zIndex={999}
        >
          {options.map((option, index) => (
            <Pressable
              key={option}
              onPress={() => {
                onValueChange(option);
                setIsOpen(null);
              }}
            >
              <Box
                px={2}
                py={1.5}
                borderBottomWidth={index < options.length - 1 ? 1 : 0}
                borderBottomColor="#F0F0F0"
                bg={
                  selectedValue === option
                    ? "#F5F5F5"
                    : GlobalStyles.colors.white
                }
              >
                <Text fontSize="10px" color={GlobalStyles.colors.blue}>
                  {option}
                </Text>
              </Box>
            </Pressable>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ReservationAnalyticsView = ({ onHome }) => {
  const [selectedPeriod, setSelectedPeriod] = useState("90 Days");
  const [selectedFilter, setSelectedFilter] = useState("Week");
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const toast = useToast();
  let user = useSelector((state) => state.user.user);

  const [dimensions, setDimensions] = useState(Dimensions.get("window"));

  useEffect(() => {
    const subscription = Dimensions.addEventListener("change", ({ window }) => {
      setDimensions(window);
    });
    return () => subscription?.remove();
  }, []);

  const screenWidth = dimensions.width;
  const isMobile = screenWidth < 768;

  const periods = ["7 Days", "14 Days", "30 Days", "90 Days"];

  const getFilterPeriod = (filter) => {
    const filterMap = {
      Day: "day",
      Week: "week",
      Month: "month",
    };
    return filterMap[filter] || "week";
  };

  const fetchDashboardAnalytics = async () => {
    setIsLoading(true);
    try {
      const apiPeriod = getFilterPeriod(selectedFilter);

      const response = await axios.get(
        `${API_BASE_URL}/api/analytics/dashboard?period=${apiPeriod}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        },
      );

      if (response.data) {
        setAnalyticsData(response.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.show({
        render: () => (
          <Toast
            color={GlobalStyles.colors.red}
            message={
              error.response?.data?.message || "Failed to fetch analytics!"
            }
          />
        ),
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchChartData = async (period) => {
    try {
      const apiPeriod = getFilterPeriod(period);

      const response = await axios.get(
        `${API_BASE_URL}/api/analytics/chart?period=${apiPeriod}`,
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        },
      );

      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        const allData = response.data.data;

        const transformedData = {
          labels: allData.map((item) => {
            const date = new Date(item.date);
            return date.toLocaleDateString("en-US", { weekday: "short" });
          }),
          values: allData.map((item) => item.count || 0),
        };
        setChartData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    }
  };

  useEffect(() => {
    if (user?.token) {
      fetchDashboardAnalytics();
      fetchChartData(selectedFilter);
    }
  }, [selectedFilter, user?.token]);

  const stats = analyticsData
    ? [
        {
          title: "Total Seats",
          value:
            analyticsData.metrics?.totalSeats?.value?.toLocaleString() || "0",
          change:
            analyticsData.metrics?.totalSeats?.change !== undefined &&
            analyticsData.metrics.totalSeats.change !== 0
              ? `${analyticsData.metrics.totalSeats.change >= 0 ? "+" : ""}${analyticsData.metrics.totalSeats.change.toFixed(1)}%`
              : "+0.0%",
          trendData: analyticsData.metrics?.totalSeats?.trend || [],
        },
        {
          title: "Total Cost",
          value: `$${analyticsData.metrics?.totalCost?.value?.toLocaleString() || "0"}`,
          change:
            analyticsData.metrics?.totalCost?.change !== undefined &&
            analyticsData.metrics.totalCost.change !== 0
              ? `${analyticsData.metrics.totalCost.change >= 0 ? "+" : ""}${analyticsData.metrics.totalCost.change.toFixed(1)}%`
              : "+0.0%",
          trendData: analyticsData.metrics?.totalCost?.trend || [],
        },
        {
          title: "Active Services",
          value:
            analyticsData.metrics?.activeServices?.value?.toString() || "0",
          change:
            analyticsData.metrics?.activeServices?.change !== undefined &&
            analyticsData.metrics.activeServices.change !== 0
              ? `${analyticsData.metrics.activeServices.change >= 0 ? "+" : ""}${analyticsData.metrics.activeServices.change.toFixed(1)}%`
              : "+0.0%",
          trendData: analyticsData.metrics?.activeServices?.trend || [],
        },
      ]
    : [
        { title: "Total Seats", value: "0", change: "+0.0%", trendData: [] },
        { title: "Total Cost", value: "$0", change: "+0.0%", trendData: [] },
        {
          title: "Active Services",
          value: "0",
          change: "+0.0%",
          trendData: [],
        },
      ];

  return (
    <VStack w="100%" space={4}>
      <HStack w="100%" justifyContent="flex-end" px={4} mt={2}>
        <HomeButton onPress={onHome} />
      </HStack>

      <VStack w="100%" alignItems="center" space={5} mt={1} pb={8}>
        {isLoading && (
          <Center mt={4}>
            <Spinner color={GlobalStyles.colors.blue} size="lg" />
          </Center>
        )}

        <VStack
          w="100%"
          maxW="600px"
          alignItems="center"
          space={5}
          px={isMobile ? 2 : 4}
        >
          <HStack
            space={3}
            flexDirection={isMobile ? "column" : "row"}
            justifyContent="center"
            w="100%"
            zIndex={100}
          >
            {stats.map((stat, index) => {
              const isThisDropdownOpen = openDropdownIndex === index;

              return (
                <Box
                  key={index}
                  w={isMobile ? "100%" : "180px"}
                  minW={isMobile ? "100%" : "160px"}
                  maxW="200px"
                  h="120px"
                  borderRadius={12}
                  overflow="hidden"
                  zIndex={isThisDropdownOpen ? 999 : 1}
                  mb={isMobile ? 2 : 0}
                >
                  <LinearGradient
                    colors={[
                      GlobalStyles.colors.statGradientTop,
                      GlobalStyles.colors.statGradientBottom,
                    ]}
                    start={{ x: 0.5, y: 0 }}
                    end={{ x: 0.5, y: 1 }}
                    style={{
                      paddingTop: 12,
                      paddingLeft: 12,
                      paddingRight: 12,
                      paddingBottom: 10,
                      height: "100%",
                      borderRadius: 12,
                    }}
                  >
                    <VStack justifyContent="space-between" h="100%">
                      <HStack
                        justifyContent="space-between"
                        alignItems="flex-start"
                        w="100%"
                        zIndex={isThisDropdownOpen ? 999 : 1}
                      >
                        <Text
                          fontSize="11px"
                          fontWeight="400"
                          color={GlobalStyles.colors.white}
                        >
                          {stat.title}
                        </Text>
                        <StatCardDropdown
                          selectedValue={selectedPeriod}
                          onValueChange={(value) => setSelectedPeriod(value)}
                          options={periods}
                          cardIndex={index}
                          isOpen={isThisDropdownOpen}
                          setIsOpen={setOpenDropdownIndex}
                        />
                      </HStack>

                      <HStack
                        justifyContent="space-between"
                        alignItems="flex-end"
                        w="100%"
                        zIndex={0}
                        mt={3}
                      >
                        <VStack space={0.5}>
                          <Text
                            fontSize="17px"
                            fontWeight="500"
                            color="#FFC107"
                            lineHeight="21px"
                          >
                            {stat.value}
                          </Text>
                          <HStack alignItems="center" space={1}>
                            <Icon
                              as={Ionicons}
                              name="trending-up"
                              size="xs"
                              color="rgba(255, 255, 255, 0.9)"
                            />
                            <Text
                              fontSize="9px"
                              fontWeight="400"
                              color="rgba(255, 255, 255, 0.9)"
                            >
                              {stat.change}
                            </Text>
                          </HStack>
                        </VStack>
                        <Box zIndex={0}>
                          <MiniTrendChart data={stat.trendData} />
                        </Box>
                      </HStack>
                    </VStack>
                  </LinearGradient>
                </Box>
              );
            })}
          </HStack>

          <HStack space={3} mt={1} flexWrap="wrap" justifyContent="center">
            {["Month", "Week", "Day"].map((filter) => (
              <Pressable key={filter} onPress={() => setSelectedFilter(filter)}>
                {selectedFilter === filter ? (
                  <Box borderRadius={25} overflow="hidden">
                    <LinearGradient
                      colors={[
                        GlobalStyles.colors.blue,
                        GlobalStyles.colors.statGradientBottom,
                      ]}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      locations={[0.155, 0.895]}
                      style={{
                        paddingHorizontal: isMobile ? 20 : 24,
                        paddingVertical: 8,
                        minWidth: isMobile ? 80 : 100,
                        alignItems: "center",
                      }}
                    >
                      <Text fontSize="14px" fontWeight="500" color="#FFC107">
                        {filter}
                      </Text>
                    </LinearGradient>
                  </Box>
                ) : (
                  <Box
                    borderRadius={25}
                    borderWidth={2}
                    borderColor={GlobalStyles.colors.blue}
                    px={isMobile ? 5 : 6}
                    py={2}
                    minW={isMobile ? 80 : 100}
                    alignItems="center"
                    bg={GlobalStyles.colors.white}
                  >
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      color={GlobalStyles.colors.blue}
                    >
                      {filter}
                    </Text>
                  </Box>
                )}
              </Pressable>
            ))}
          </HStack>

          <ReservationChart
            selectedMetric="Total Reservations"
            selectedPeriod={selectedFilter}
            onMetricChange={() => {}}
            onPeriodChange={() => {}}
            openChartDropdown={null}
            setOpenChartDropdown={() => {}}
            chartData={chartData}
          />
        </VStack>
      </VStack>
    </VStack>
  );
};

const GradientActionButton = ({ label, onPress }) => {
  return (
    <Pressable onPress={onPress}>
      <LinearGradient
        colors={[
          GlobalStyles.colors.gradientBlueStart,
          GlobalStyles.colors.gradientBlueEnd,
        ]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
        style={{
          borderWidth: 1,
          borderColor: GlobalStyles.colors.gradientBlueStart,
          borderRadius: 10,
          width: 190,
          height: 40,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Text
          fontWeight="bold"
          fontSize="sm"
          color={GlobalStyles.colors.mustard}
          fontFamily={GlobalStyles.text.fontFamily}
        >
          {label}
        </Text>
      </LinearGradient>
    </Pressable>
  );
};

const BusinessReservation = ({
  onBack,
  onAnalytics,
  initialTab = "menu",
  showBackButton = true,
}) => {
  const [activeView, setActiveView] = useState(initialTab);

  const handleGoHome = () => {
    setActiveView("menu");
  };

  return (
    <ScrollView w="100%" flex={1}>
      <Center>
        <Stack
          mt={4}
          w={["92%", 600, 800]}
          alignItems="center"
          justifyContent="center"
        >
          {onBack && showBackButton && activeView === "menu" && (
            <HStack w="100%" mb={3} px={4}>
              <Pressable onPress={onBack}>
                <HStack alignItems="center" space={2}>
                  <Text
                    color={GlobalStyles.colors.blue}
                    fontWeight="500"
                    fontSize={14}
                    fontFamily={GlobalStyles.text.fontFamily}
                  >
                    ← Back to Services
                  </Text>
                </HStack>
              </Pressable>
            </HStack>
          )}

          <Box alignItems="center" w="100%" maxW="600px">
            {activeView === "menu" ? (
              <VStack alignItems="center" space={2.5}>
                <GradientActionButton
                  label="Check Reservation"
                  onPress={() => setActiveView("check")}
                />
                <GradientActionButton
                  label="Reservation Analytics"
                  onPress={() => setActiveView("analytics")}
                />
                <GradientActionButton
                  label="Reservation Settings"
                  onPress={() => setActiveView("settings")}
                />
                <GradientActionButton
                  label="Make Reservation"
                  onPress={() => setActiveView("makeReservation")}
                />
              </VStack>
            ) : activeView === "check" ? (
              <CheckReservationView onHome={handleGoHome} />
            ) : activeView === "analytics" ? (
              <ReservationAnalyticsView onHome={handleGoHome} />
            ) : activeView === "settings" ? (
              <ReservationSettingsView onHome={handleGoHome} />
            ) : activeView === "makeReservation" ? (
              <MakeReservationView onHome={handleGoHome} />
            ) : null}
          </Box>
        </Stack>
      </Center>
    </ScrollView>
  );
};
export default BusinessReservation;